/* =========================================================================
   Tiny DOM helpers + the screen registry. `show(name)` hides every top-level
   screen and reveals one — the typed equivalent of the prototype's `show()`,
   extended to also cover the new auth/history screens.
   ========================================================================= */

export function byId<T extends HTMLElement = HTMLElement>(id: string): T {
  const e = document.getElementById(id);
  if (!e) throw new Error('Missing element #' + id);
  return e as T;
}

/** Logical screen name -> element id. Order does not matter; all are mutually exclusive. */
export const SCREEN_IDS: Record<string, string> = {
  welcome: 'screenWelcome',
  login: 'screenLogin',
  register: 'screenRegister',
  history: 'screenHistory',
  home: 'screenHome',
  roundintro: 'screenRoundIntro',
  preroll: 'screenPreroll',
  play: 'screenPlay',
  bonus: 'screenBonus',
  manna: 'screenManna',
  match3: 'screenMatch3',
  marymatch3: 'screenMaryMatch3',
  nextround: 'screenNextRound',
  results: 'screenResults',
  feast: 'screenFeast',
  collections: 'screenCollections',
  pilgrimage: 'screenPilgrimage',
  monastery: 'screenMonastery',
  leaderboard: 'screenLeaderboard',
  // Friendly Play (PLAN-gameplay-round2.md Part 2): the study-area picker and
  // the untimed question card. Separate screens from `play` so a study set can
  // never share DOM with a scored round.
  friendly: 'screenFriendly',
  friendlyplay: 'screenFriendlyPlay',
};

export type ScreenName = keyof typeof SCREEN_IDS;

let screensCache: Record<string, HTMLElement> | null = null;

export function screens(): Record<string, HTMLElement> {
  if (!screensCache) {
    screensCache = {};
    for (const [name, id] of Object.entries(SCREEN_IDS)) screensCache[name] = byId(id);
  }
  return screensCache;
}

/** Optional hook (set by telemetry) — fires on every screen transition. */
export let onScreenShown: ((name: string) => void) | null = null;
export function setOnScreenShown(fn: (name: string) => void): void {
  onScreenShown = fn;
}

export function show(name: string): void {
  const s = screens();
  for (const k in s) s[k].classList.toggle('hidden', k !== name);
  if (onScreenShown) onScreenShown(name);
}

export function isVisible(name: string): boolean {
  return !screens()[name].classList.contains('hidden');
}

/** Is a modal overlay currently up? True when any `[aria-modal="true"]` element
    is live — i.e. not itself `aria-hidden`, and not inside a `.hidden` ancestor
    (the leave sheet, candle card and rank-up overlay stay mounted while idle).
    Dynamic moments (momentOverlay / prayerBreak / breakCard / roundPicker) mount
    and remove their root, so presence alone counts for them. Pure over `root`. */
export function isModalOpen(root: ParentNode = document): boolean {
  for (const el of Array.from(root.querySelectorAll<HTMLElement>('[aria-modal="true"]'))) {
    if (el.getAttribute('aria-hidden') === 'true') continue;
    if (el.closest('.hidden')) continue;
    return true;
  }
  return false;
}
